import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PawPrint, Home, Users, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { animalsPublicApi } from '@/features/public/animalsPublicApi';
import { adoptionApi } from '@/features/adoption/api';

export default function Dashboard() {
  const { isAdmin, isCustomer } = useAuth();
  const [statusCounts, setStatusCounts] = useState<{ available?: number; inAdoption?: number; adopted?: number }>({});
  const [adoptionCount, setAdoptionCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      try {
        const res = await animalsPublicApi.statusCounts();
        if (!cancelled) setStatusCounts(res || {});
      } catch {
        if (!cancelled) setStatusCounts({});
      }
    };
    loadCounts();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isCustomer) return;
    let cancelled = false;
    const loadAdoptionCount = async () => {
      try {
        const res: any = await adoptionApi.listApplications({
          status: 'SUBMITTED',
          filter: { limit: 100, fields: ['id'] }, // Try to get list to count length, assuming manageable number for 'new'
        });
        // If response supports total, use it. Otherwise use length.
        const count = Array.isArray(res) ? res.length : Number(res?.total ?? res?.data?.length ?? 0);
        if (!cancelled) setAdoptionCount(Number.isFinite(count) ? count : 0);
      } catch {
        if (!cancelled) setAdoptionCount(null);
      }
    };
    loadAdoptionCount();
    return () => {
      cancelled = true;
    };
  }, [isCustomer]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <img src="/logo-sabuesos.png" alt="Sabuesos" className="h-12 w-12 mb-3 object-contain" />
          <h1 className="text-4xl font-bold mb-2">Panel de Control</h1>
          <p className="text-muted-foreground">Bienvenido al sistema de gestión de adopciones</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Disponibles</p>
            <p className="text-3xl font-bold text-green-600">{statusCounts.available ?? '—'}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">En proceso de adopción</p>
            <p className="text-3xl font-bold text-amber-600">{statusCounts.inAdoption ?? '—'}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Adoptados</p>
            <p className="text-3xl font-bold text-blue-700">{statusCounts.adopted ?? '—'}</p>
          </Card>
          {!isCustomer && (
            <Card className="p-4 h-full bg-orange-100 border-orange-200">
              <p className="text-xs text-orange-800 font-bold uppercase tracking-wide">SOLICITUDES POR REVISAR</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-3xl font-bold text-orange-700">
                  {adoptionCount ?? '—'}
                </p>
                <FileText className="h-5 w-5 text-orange-700" />
              </div>
            </Card>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link to="/animals">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <PawPrint className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Animales</h3>
                  <p className="text-sm text-muted-foreground">Gestionar animales</p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to="/shelters">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                  <Home className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Entidades</h3>
                  <p className="text-sm text-muted-foreground">Gestionar entidades</p>
                </div>
              </div>
            </Card>
          </Link>

          {!isCustomer && (
            <Link to="/adoptions/manage">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Solicitudes</h3>
                    <p className="text-sm text-muted-foreground">Revisar pedidos de adopción</p>
                  </div>
                </div>
              </Card>
            </Link>
          )}

          {isAdmin && (
          <Link to="/users">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-info/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-info" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Usuarios</h3>
                  <p className="text-sm text-muted-foreground">Gestionar usuarios</p>
                </div>
              </div>
            </Card>
          </Link>
          )}
        </div>

        <Card className="mt-8 p-8 bg-gradient-hero">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold mb-4">Ayudando a encontrar hogares</h2>
            <p className="text-muted-foreground mb-6">
              Este sistema te permite gestionar de forma eficiente el proceso de adopción de animales,
              desde el registro inicial hasta encontrar el hogar perfecto para cada mascota.
            </p>
            {!isCustomer && (
            <Button asChild>
              <Link to="/animals/new">
                <PawPrint className="mr-2 h-4 w-4" />
                Registrar Nuevo Animal
              </Link>
            </Button>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
